import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    TrendingUp, TrendingDown, Pause, Receipt, AlertTriangle, ArrowRight,
} from 'lucide-react';
import CardInfo from './CardInfo';

/**
 * ActiveAlerts — vertical list, severity color-coded.
 * Reads `data.activeAlerts[]` from /api/command/:concessionaireId.
 *
 * Sprint 3.6: per-alert-type icon + "View all" link to /v3/anomalies.
 */
const TYPE_ICON = {
    sales_spike:        TrendingUp,
    sales_drop:         TrendingDown,
    zero_sales_streak:  Pause,
    invoice_gap:        Receipt,
};

function TypeIcon({ alertType }) {
    const Icon = TYPE_ICON[alertType] || AlertTriangle;
    return <Icon size={14} aria-hidden="true" />;
}

export default function ActiveAlerts({ alerts }) {
    const { t } = useTranslation();
    const list = Array.isArray(alerts) ? alerts : [];

    return (
        <div className="v3-card v3-cc-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ margin: 0 }}>
                        {t('Active Alerts')} {list.length > 0 && <span className="v3-cc-card-meta">({list.length})</span>}
                    </h3>
                    <CardInfo title={t('About Active Alerts')}>
                        <p>
                            {t('This card lists')} <strong>{t('open anomaly alerts')}</strong> — {t('automatically detected issues that have not yet been acknowledged or resolved.')}
                        </p>
                        <p>
                            <strong>{t('Alert types')}:</strong> {t('sales spikes and drops (≥2σ from the tenant baseline), zero-sales streaks, invoice gaps (missing sequential invoices), and flight-risk (revenue-share anomaly) escalations.')}
                        </p>
                        <p>
                            <strong>{t('Severity')}</strong> {t('is colour-coded — critical (red) outranks warning (amber) outranks info. The dot on the left shows each alert’s severity.')}
                        </p>
                        <p>
                            {t('Use “View all” to open the full Anomalies page where alerts can be acknowledged or resolved.')}
                        </p>
                    </CardInfo>
                </div>
                <Link
                    to={`${process.env.PUBLIC_URL}/v3/anomalies`}
                    className="v3-cc-card-meta"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                >
                    {t('View all')} <ArrowRight size={12} />
                </Link>
            </div>
            {list.length === 0 ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>{t('No active alerts.')}</div>
            ) : (
                <div className="v3-cc-alerts-list">
                    {list.map((a) => {
                        const sev = a.severity || 'low';
                        const ts = a.created_at ? new Date(a.created_at).toLocaleString() : '';
                        const alertType = a.alert_type || a.category;
                        return (
                            <div key={a.id} className={`v3-cc-alert ${sev}`}>
                                <span className={`v3-cc-dot ${sev}`} style={{ marginTop: 5 }} aria-hidden="true" />
                                <span className="visually-hidden">{t('{{sev}} severity', { sev })}: </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <TypeIcon alertType={alertType} />
                                        <span style={{ flex: 1, minWidth: 0 }}>{a.title || a.category || t('Alert')}</span>
                                    </div>
                                    {a.message && <div className="msg">{a.message}</div>}
                                    <div className="ts">
                                        {alertType && <span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{alertType.replace(/_/g, ' ')}</span>}
                                        {ts && <span> · {ts}</span>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
